import {
	BagPaymentBankAccount,
	BagPaymentCurrency,
	BagPaymentFilters,
	BagPaymentKind,
	BagPaymentListParseFilters,
	BagPaymentOrganization,
	BagPaymentParametersStatus,
	BagPaymentReservationStatus,
} from '@/bag/bag-payment-list/bag-payment-list.model';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import { CancellationStatusesListReservation, RejectedStatusesListReservation, Status } from '@/common/enums/Status';
import { FiltersComponent } from '@/common/filters.component';
import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, input, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-bag-payment-list-table-filters',
	templateUrl: './bag-payment-list-table-filters.component.html',
	styleUrls: ['./bag-payment-list-table-filters.component.scss'],
	providers: [DatePipe],
})
export class BagPaymentListTableFiltersComponent extends FiltersComponent implements AfterViewInit {
	readonly status = input<BagPaymentParametersStatus[]>([]);
	readonly paymentKinds = input<BagPaymentKind[]>([]);
	readonly currency = input<BagPaymentCurrency[]>([]);
	readonly bankAccounts = input<BagPaymentBankAccount[]>([]);
	readonly reservationStatus = input<BagPaymentReservationStatus[]>([]);

	readonly parseFilters = signal<BagPaymentListParseFilters>({
		amount__gte: null,
		amount__lt: null,
		payment__gte: null,
		payment__lte: null,
		posting__gte: null,
		posting__lte: null,
		reservation_status: [],
		reservation_time__gte: null,
		confirmation_time__gte: null,
	});

	readonly autocompleteResetKey = signal(0);

	readonly organizationApiUrl = `${environment.apiUrl}/organization/`;
	readonly organizationApiSearchParam = 'is_oskar=true&limit=50&name';
	readonly agencyApiSearchParam = 'is_oskar=false&limit=50&name';

	filters: BagPaymentFilters = {
		limit: '999999',
		organization_id: null,
		reservation_seller: [],
		status__in: [],
		kind__in: [],
		reservation_status: [],
		table_id__in: null,
		table_name__in: null,
		invoice_number__icontains: null,
		amount__gte: null,
		amount__lt: null,
		payment__gte: null,
		payment__lte: null,
		posting__gte: null,
		posting__lte: null,
		currency__in: [],
		bank_account_incoming__icontains: null,
		bank_account_posting__in: null,
	};

	readonly dateConvert: Array<keyof BagPaymentListParseFilters> = [
		'payment__gte',
		'payment__lte',
		'posting__gte',
		'posting__lte',
	];

	protected filtersDefinition = {
		kind__in: {
			type: 'string',
			isArray: true,
		},
		reservation_status: {
			type: 'string',
			isArray: true,
		},
		reservation_seller: {
			type: 'string',
			isArray: true,
		},
		payment__lte: {
			type: 'string',
			isArray: false,
		},
		currency__in: {
			type: 'string',
			isArray: false,
		},
		status__in: {
			type: 'string',
			isArray: true,
		},
	};

	constructor(
		protected api: Data2Service,
		protected route: ActivatedRoute,
		protected router: Router,
		protected date: DateService,
		protected datePipe: DatePipe,
	) {
		super(api, route, router);
	}

	ngAfterViewInit(): void {
		this.checkQueryParamsAndReturnSevenDaysBeforeDate().subscribe(async (date) => {
			if (!this.parseFilters().payment__gte) {
				this.updateParseFilter('payment__gte', this.datePipe.transform(date, 'yyyy-MM-dd'));
				await this.filtersSave();
				this.filtersLoad();
			}
		});
	}

	updateParseFilter(key: keyof BagPaymentListParseFilters, value: string | number | Status[] | null): void {
		this.parseFilters.update((current) => ({
			...current,
			[key]: value,
		}));
	}

	onOrganizationSelected(organization: (BagPaymentOrganization & Record<string, unknown>) | null): void {
		this.filters.organization_id = organization?.id ?? null;
	}

	onAgenciesSelected(agencies: Array<BagPaymentOrganization & Record<string, unknown>>): void {
		this.filters.reservation_seller = agencies.map((agency) => agency.id);
	}

	filtersSave(): Promise<void> {
		this.convertSaveFilters();
		this.filtersClose();
		return new Promise((resolve, reject) => {
			this.api.put('/userFilters/' + this.userFilters, this.filters, (_result, error) => {
				if (error) {
					reject();
					return;
				}
				resolve();
			});
		});
	}

	filtersClear(): void {
		for (const key in this.filters) {
			if (Array.isArray(this.filters[key])) {
				this.filters[key] = [];
				continue;
			}
			this.filters[key] = null;
		}

		this.parseFilters.set({
			amount__gte: null,
			amount__lt: null,
			payment__gte: null,
			payment__lte: null,
			posting__gte: null,
			posting__lte: null,
			reservation_status: [],
			reservation_time__gte: null,
			confirmation_time__gte: null,
		});
		this.autocompleteResetKey.update((key) => key + 1);
	}

	protected convertSaveFilters(): void {
		const parseFilters = this.parseFilters();

		Object.keys(this.filters).forEach((key) => {
			if (this.filters[key] === '' && !this.filters[key]) {
				this.filters[key] = null;
			}
			if (
				!Array.isArray(this.filters[key]) &&
				parseFilters[key] != null &&
				parseFilters[key] !== '' &&
				!this.dateConvert.includes(key as never)
			) {
				this.filters[key] = (Number(parseFilters[key]) * 100).toFixed(0);
			}
			if (this.dateConvert.includes(key as never) && parseFilters[key] != null) {
				this.filters[key] = this.datePipe.transform(parseFilters[key] as string, 'yyyy-MM-dd');
			}
		});

		Object.keys(parseFilters).forEach((key) => {
			const value = parseFilters[key as keyof BagPaymentListParseFilters];
			if (value == null || value === '') {
				this.filters[key] = null;
			}
		});

		this.checkStatusFilter();
	}

	protected filtersAfterLoad(): void {
		this.parseFilters.set({
			payment__gte: this.filters.payment__gte,
			payment__lte: this.filters.payment__lte,
			posting__gte: this.filters.posting__gte,
			posting__lte: this.filters.posting__lte,
			amount__gte: this.filters.amount__gte ? (Number(this.filters.amount__gte) * 0.01).toFixed(2) : null,
			amount__lt: this.filters.amount__lt ? (Number(this.filters.amount__lt) * 0.01).toFixed(2) : null,
			reservation_status: this.mapReservationStatusToParse(this.filters.reservation_status),
			reservation_time__gte: null,
			confirmation_time__gte: null,
		});
	}

	protected filters2url(filters: BagPaymentFilters): void {
		if (!filters || filters instanceof Array) {
			this.selected.emit({});
			return;
		}
		filters.on = 1;
		this.router.navigate(['.'], {
			relativeTo: this.route,
			queryParams: filters,
			preserveFragment: true,
		});
	}

	private checkStatusFilter(): void {
		this.filters.reservation_status = [];
		this.parseFilters().reservation_status?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					this.filters.reservation_status.push(...CancellationStatusesListReservation);
					break;
				case Status.REJECTED:
					this.filters.reservation_status.push(...RejectedStatusesListReservation);
					break;
				case Status.OPTION:
					this.filters.reservation_status.push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.filters.reservation_status.push(Status.CONFIRMED);
					break;
				default:
					this.filters.reservation_status = [];
					break;
			}
		});
		this.filters2url(this.filters);
	}

	private mapReservationStatusToParse(statuses: Status[] | undefined): Status[] {
		const mapped: Status[] = [];
		statuses?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					mapped.push(Status.CANCELED);
					break;
				case Status.REJECTED_EXPIRATION:
					mapped.push(Status.REJECTED);
					break;
				case Status.OPTION:
					mapped.push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					mapped.push(Status.CONFIRMED);
					break;
			}
		});
		return mapped;
	}
}
